<template>
  <el-dialog
    class="dialog-inner"
    :model-value="visible"
    :before-close="() => {
      emit('close');
    }"
    width="500"
  >
    <template #header>
      <div class="my-header">
        <h4 class="px-2 text-xl text-[#344767] font-semibold">
          账号提现
        </h4>
      </div>
    </template>

    <div class="flex justify-center items-center h-[66px] bg-[var(--color-gray-hover)] mb-5">
      <div class="step-node" :class="{ 'active': step === 1 }">
        <svg-icon icon="tabler:arrow-bar-up" class="!w-5 !h-5" />
        <span>提交申请</span>
      </div>

      <svg-icon icon="lsicon:right-filled" class="w-4 h-4 mx-6 !text-[##BFBFBF]" />

      <div class="step-node" :class="{ 'active': step === 2 }">
        <svg-icon icon="charm:circle-tick" class="!w-4 !h-4" />
        <span>确认提现</span>
      </div>
    </div>

    <div class="step-form">
      <template v-if="step === 1">
        <div class="flex items-center mb-3">
          <span class="text-[#909399] mr-4">企业名称</span>
          <span>{{ props.data.bizName }}</span>
        </div>

        <div class="flex items-center mb-3">
          <span class="text-[#909399] mr-4">当前余额</span>
          <span>{{ props.data.availableBalance }}元</span>
        </div>
      </template>

      <el-form
        ref="formRef"
        require-asterisk-position="right"
        label-position="top"
        :model="formValues"
        :rules="formRules"
      >
        <el-form-item v-if="step === 1" prop="amount" label="提现金额">
          <el-input
            v-model="formValues.amount"
            size="large"
            autocomplete="new-password"
            placeholder="请输入提现金额"
            @keydown="(e: KeyboardEvent | Event) => {
              if ((e as KeyboardEvent).keyCode === 13) {
                e.preventDefault();
                onConfirm();
              }
            }"
          >
            <template #suffix>
              元
            </template>
          </el-input>
        </el-form-item>
        <el-form-item v-if="step === 2" label="短信验证码" prop="code">
          <el-input
            v-model="formValues.code"
            size="large"
            class="w-[353px]"
            placeholder="请输入验证码"
            autocomplete="new-password"
            @keydown="(e: KeyboardEvent | Event) => {
              if ((e as KeyboardEvent).keyCode === 13) {
                e.preventDefault();
                onConfirm();
              }
            }"
          />
          <sms-button
            ref="smsBtnRef"
            class="!left-[368px]"
            @click="sendSms"
          />
        </el-form-item>
      </el-form>

      <!-- 说明 -->
      <div class="flex flex-col bg-[#E8F2FF] text-[#3194FA] text-[12px] mt-5 px-3 py-2 border border-[#D1E4FF] rounded-sm">
        <div>说明：</div>
        <template v-if="step === 1">
          <div>商户支付宝的提现收款账户为商户的企业支付宝</div>
          <div>工作时间一般10分钟左右到账，非工作时间2小时左右到账</div>
        </template>
        <template v-else>
          <div>提现验证码{{ sended ? '已' : '将' }}发送至 {{ fuzzyMobile(props.payPhone) }}</div>
        </template>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <el-button @click="emit('close')">
          取消
        </el-button>
        <el-button type="primary" :loading="loading" @click="onConfirm">
          {{ step === 1 ? '申请提现' : '确认提现' }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { checkNumber, checkSmsCode } from '@core/tools';
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { SmsButton, SvgIcon } from '@vue3/components';
import { nextTick, PropType, ref, toRaw, watch } from 'vue';
import { IFinanceWallet } from '@core/main/types';
import { ESmsType, sendSmscode } from '@core/api';
import { fuzzyMobile } from '@core/tools/src/commonUtil';

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object as PropType<IFinanceWallet>,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  term: {
    type: String as PropType<'server' | 'company' | 'agent' | 'saas'>,
    default: 'server',
  },
  payPhone: {
    type: String,
    default: '',
  },
});

const sended = ref(false);
const step = ref(1);
const formRef = ref<FormInstance>();
const smsBtnRef = ref<InstanceType<typeof SmsButton>>(null);
const formValues = ref({
  amount: '',
  code: '',
});

const formRules = ref<FormRules<any>>({
  amount: [
    {
      required: true,
      message: '请输入提现金额',
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (checkNumber(String(formValues.value.amount), '提现金额', 2)) {
          callback(checkNumber(String(formValues.value.amount), '提现金额', 2));
        }
        callback();
      },
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (!formValues.value.amount || Number(formValues.value.amount) <= 0) {
          callback(new Error('提现金额不能为0'));
        }
        callback();
      },
    },
  ],
  code: [
    {
      required: true,
      message: '请输入验证码',
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (checkSmsCode(formValues.value.code)) {
          callback(checkSmsCode(formValues.value.code));
        }
        callback();
      },
    },
  ],
});

async function sendSms() {
  try {
    smsBtnRef.value?.showLoading();

    await sendSmscode(ESmsType.SplitBill, props.payPhone);

    sended.value = true;
    smsBtnRef.value?.countdown();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  setTimeout(() => {
  }, 1300);
}

function onConfirm() {
  formRef.value?.validate(async (valid) => {
    if (valid) {
      if (step.value === 2) {
        const params = {
          accountId: Number(props.data.accountId),
          ...toRaw(formValues.value),
        };

        emit('confirm', params);

        return;
      }

      if (Number(formValues.value.amount) > Number(props.data.availableBalance)) {
        ElMessage.error('提现金额不能大于当前余额');
        return;
      }

      step.value = 2;
    }
  });
}

function resetForm() {
  for (const key in formValues.value) {
    formValues.value[key] = undefined;
  }
}

watch(
  () => props.visible,
  (val) => {
    if (val) {
      nextTick(() => {
        formRef.value?.resetFields();
      });
    } else {
      step.value = 1;
      sended.value = false;
      resetForm();
    }
  },
);
</script>

<style lang="scss" scoped>
:deep(.dialog-inner .el-dialog__body) {
  overflow-y: unset !important;
}

.step-node {
  @apply flex items-center justify-center;

  &.active {
    @apply text-[#1777FF];
  }

  span {
    @apply ml-1;
  }
}
</style>
